import React, { useCallback, useEffect, useState, useRef } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchInternFunctionalRolesSpecial,
  fetchOrganizationFunctionalRoles,
  fetchOrganizationFunctionalRolesSpecial,
  fetchFunctionalRolesSpecialaByRole,
} from "../../../Apis/FunctionalManagment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import dayjs from "dayjs";
import {
  applyClientToolbarFilters,
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

/** Show in list until unassigned date passes (assigned date may be in the future). */
const isActiveInternRoleAssignment = (unassignedOn) => {
  if (!unassignedOn || unassignedOn === "-" || unassignedOn === "") {
    return true;
  }
  const unassignedDate = dayjs(unassignedOn).startOf("day");
  if (!unassignedDate.isValid()) {
    return true;
  }
  return !unassignedDate.isBefore(dayjs().startOf("day"));
};

const DEFAULT_COLUMNS = [
  {
    field: "intern_name",
    label: "Intern Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "intern_code",
    label: "Intern Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "function_role",
    label: "Functional Role",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "role_specialization",
    label: "Role Specialization",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "assigned_on",
    label: "Assigned On",
    visible: true,
    width: 190,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "unassigned_on",
    label: "Unassigned On",
    visible: true,
    width: 190,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "intern_functional_role_id",
  sortOrder: "desc",
};

const INTERN_ROLE_TOOLBAR_FILTERS = [
  { urlKey: "functional_role_id", field: "organization_functional_role_id" },
  {
    urlKey: "role_specialization_id",
    field: "organization_functional_role_specialization_id",
  },
];

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

function InternRoleList() {
  const [func, setFunc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [functionalRoles, setFunctionalRoles] = useState([]);
  const [roleSpecializations, setRoleSpecializations] = useState([]);

  const { userData } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const org = userData?.organization;
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);
  const selectedRoleId = searchParams.get("functional_role_id") || "";

  function formatDateTime(date) {
    if (!date || date === "-") return "";

    const parsedDate = dayjs(date);
    return parsedDate.isValid()
      ? parsedDate.format("DD-MM-YYYY hh:mm A")
      : "";
  }

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetchOrganizationFunctionalRoles(org.organization_id)
      .then((data) => {
        if (cancelled) return;
        setFunctionalRoles(asOptionList(data, ["functional", "data"]));
      })
      .catch((error) => {
        console.error("Failed to load functional roles", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    const loadSpecs = async () => {
      try {
        const data = selectedRoleId
          ? await fetchFunctionalRolesSpecialaByRole(
              org.organization_id,
              selectedRoleId
            )
          : await fetchOrganizationFunctionalRolesSpecial(org.organization_id);
        if (cancelled) return;
        setRoleSpecializations(asOptionList(data, ["functional", "data"]));
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load role specializations", error);
          setRoleSpecializations([]);
        }
      }
    };
    loadSpecs();
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id, selectedRoleId]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(
          `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `intern_functional_roles_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          INTERN_ROLE_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "intern_functional_role_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/intern-func-role/filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          const oldData = await fetchInternFunctionalRolesSpecial(org.organization_id);
          data = applyClientToolbarFilters(
            oldData?.functional?.data || [],
            searchParams,
            INTERN_ROLE_TOOLBAR_FILTERS
          );
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        let filteredData = data.filter((item) =>
          isActiveInternRoleAssignment(item.unassigned_on)
        );

        let b = filteredData.map((item) => {
          const internName =
            item.intern_name ||
            (item.intern?.org_persons?.person
              ? `${item.intern.org_persons.person.first_name || ""} ${item.intern.org_persons.person.middle_name || ""} ${item.intern.org_persons.person.last_name || ""}`.trim()
              : item.intern
                ? `${item.intern.first_name || ""} ${item.intern.middle_name || ""} ${item.intern.last_name || ""}`.trim()
                : "");

          const internCode =
            item.intern_code ||
            (item.intern?.org_persons?.person_code || item.intern?.person_code || "");

          const functionRole =
            item.functional_role ||
            item.function_role ||
            (item.function_role?.functional_role_name || "-");

          const roleSpecialization =
            item.functional_role_specialization ||
            item.role_specialization ||
            (item.role_specialization?.functional_role_specialization_name || "-");

          return {
            ...item,
            id: item.intern_functional_role_id,
            intern_name: internName,
            intern_code: internCode,
            function_role: functionRole,
            role_specialization: roleSpecialization,
            is_active: item.is_active == 1 || item.is_active === true ? "✔" : "✖",
            assigned_on: formatDateTime(item.assigned_on),
            unassigned_on: formatDateTime(item.unassigned_on),
          };
        });
        setFunc(b);
      } catch (err) {
        if (err.name === "AbortError") return;

        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch intern functional roles:", err);
          toast.error("Failed to fetch intern functional roles");
          setFunc([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  const deleteRole = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/intern-func-role/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Intern functional role deleted successfully");
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorMessage =
            data.message ||
            data.error ||
            data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
            "Failed to delete role";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete intern functional role"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id, setSearchParams]
  );


  const handleEdit = useCallback(
    (item) => {
      navigate(`/intern/functional-role/edit/${item.id || item.intern_functional_role_id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(`/intern/functional-role/view/${item.id || item.intern_functional_role_id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        delete_action={"INTERN_FUNCTIONAL_ROLE_DELETE"}
        heading={"Intern Functional Roles"}
        add_action={"INTERN_FUNCTIONAL_ROLE_ADD"}
        btnName={"Add Roles"}
        Data={func}
        tableHeaders={[
          { name: "Code", value_key: "employee_code", width: "50px" },
          { name: "Name", value_key: "name", width: "150px" },

          {
            name: "NoticePeriod Starts",
            value_key: "notice_period_start",
            width: "170px",
          },

          {
            name: "NoticePeriod Ends",
            value_key: "notice_period_end",
            width: "170px",
          },
          {
            name: "Resignation Date",
            value_key: "resignation_date",
            width: "150px",
          },
          {
            name: "Relieving Date",
            value_key: "relieving_date",
            width: "150px",
          },
          {
            name: "Interview",
            value_key: "exit_interview_done",
            width: "50px",
          },
        ]}
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <AlarmAddIcon sx={{ color: "text.secondary" }} />,
          <LogoutIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Intern Functional Roles",
          "Intern Functional Roles",
          "Add Intern Functional Roless",
          "Intern Functional Roles",
        ]}
        Route={"/intern/functional-role"}
        setData={setFunc}
        DeleteFunc={deleteRole}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="intern_functional_roles"
          primaryKey="intern_functional_role_id"
          heading="Intern Functional Roles"
          data={func}
          sortname="intern_functional_role_id"
          showActions={true}
          Route="/intern/functional-role"
          DeleteFunc={deleteRole}
          EditFunc={handleEdit}
          edit_delete_action={["INTERN_FUNCTIONAL_ROLE_EDIT", "INTERN_FUNCTIONAL_ROLE_DELETE"]}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          filterModule="intern_functional_role"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Functional Role"
                urlKey="functional_role_id"
                allLabel="All functional roles"
                options={functionalRoles}
                valueKey="organization_functional_role_id"
                labelKey="functional_role_name"
                searchParams={searchParams}
                extraClears={["role_specialization_id"]}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Role Specialization"
                urlKey="role_specialization_id"
                allLabel="All specializations"
                options={roleSpecializations}
                valueKey="organization_functional_role_specialization_id"
                labelKey="functional_role_specialization_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default InternRoleList;
